import React, { useState, useMemo, useEffect, useRef } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import { ReportTableWrapper } from '@/components/reports/ReportTableWrapper';
import { useSupplierTagSummary } from '@/api/rebates/useReports';
import { useSupplierTags } from '@/api/rebates/useSupplierTags';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import type { ReportLineType, ReportTimeFilter } from '@erp/shared-types';
import {
  LINE_TYPE_LABELS,
  LINE_TYPE_DESCRIPTIONS,
} from '@/types/rebateAnalytics';
import { ShareNetworkIcon, LinkIcon } from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const REPORT_ID = 'supplier-tag-summary';
const REPORT_TITLE = 'Supplier Tag Summary';

const SupplierTagReportPage: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedSupplierTag, setSelectedSupplierTag] =
    useState<MultiSelectOption | null>(null);
  const [lineType, setLineType] = useState<ReportLineType>('reported');
  // Default time filter based on year: 'current' for current year, 'all' for past years
  const [timeFilter, setTimeFilter] = useState<ReportTimeFilter>(
    currentYear === new Date().getFullYear() ? 'current' : 'all',
  );
  const hasSetDefaultDivision = useRef(false);
  const hasSetDefaultSupplierTag = useRef(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch supplier tags
  const { data: supplierTags, isLoading: supplierTagsLoading } =
    useSupplierTags();

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare supplier tag options
  const supplierTagOptions: MultiSelectOption[] = useMemo(() => {
    if (!supplierTags) return [];

    return supplierTags.map((tag) => ({
      id: tag.id,
      label: tag.name,
      value: tag,
    }));
  }, [supplierTags]);

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      // Fallback to last 5 years if no years available
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Default to ALL divisions when divisions are loaded (only once)
  useEffect(() => {
    if (
      divisionOptions.length > 0 &&
      selectedDivisions.length === 0 &&
      !divisionsLoading &&
      !hasSetDefaultDivision.current
    ) {
      setSelectedDivisions([...divisionOptions]);
      hasSetDefaultDivision.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionOptions.length, divisionsLoading]);

  // Default to first supplier tag option when supplier tags are loaded (only once)
  useEffect(() => {
    if (
      supplierTagOptions.length > 0 &&
      selectedSupplierTag === null &&
      !hasSetDefaultSupplierTag.current
    ) {
      setSelectedSupplierTag(supplierTagOptions[0]);
      hasSetDefaultSupplierTag.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierTagOptions.length]);

  // Update time filter when year changes
  useEffect(() => {
    const defaultTimeFilter: ReportTimeFilter =
      selectedYear < currentYear ? 'all' : 'current';
    setTimeFilter(defaultTimeFilter);
  }, [selectedYear, currentYear]);

  // Prepare line type options
  const lineTypeOptions = useMemo(() => {
    return (['estimates', 'predictions', 'reported'] as const).map((type) => ({
      id: type,
      label: LINE_TYPE_LABELS[type] || type,
      value: type,
    }));
  }, []);

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
  } = useSupplierTagSummary({
    year: selectedYear,
    divisionIds:
      selectedDivisions.length > 0
        ? selectedDivisions.map((d) => d.id)
        : undefined,
    supplierTagId: selectedSupplierTag?.id,
    lineType,
  });

  return (
    <DashboardLayout>
      <div className="p-6 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              Supplier Tag Summary - {selectedYear}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              View rebate summaries aggregated by supplier tags and months
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <ShareNetworkIcon className="h-4 w-4 mr-2" />
                Share
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                <ShareNetworkIcon className="h-4 w-4 mr-2" />
                Create share link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                <LinkIcon className="h-4 w-4 mr-2" />
                Manage shared links
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Filters Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-6">
              <SingleSelect
                label="Year"
                labelPosition="left"
                options={yearOptions}
                value={
                  yearOptions.find((opt) => opt.value === selectedYear) || null
                }
                onChange={(option) => {
                  if (option) {
                    setSelectedYear(option.value);
                  }
                }}
                placeholder="Select year..."
                searchPlaceholder="Search years..."
                shouldAutoSort={false}
              />
              <MultiSelect
                label="Divisions"
                labelPosition="left"
                options={divisionOptions}
                value={selectedDivisions}
                onChange={setSelectedDivisions}
                placeholder="Select divisions..."
                searchPlaceholder="Search divisions..."
                disabled={divisionsLoading}
                hideChips={true}
              />
              <SingleSelect
                label="Supplier Tag"
                labelPosition="left"
                options={supplierTagOptions}
                value={selectedSupplierTag}
                onChange={setSelectedSupplierTag}
                placeholder="Select supplier tag..."
                searchPlaceholder="Search supplier tags..."
                disabled={supplierTagsLoading}
              />
              <div>
                <SingleSelect
                  label="Line Type"
                  labelPosition="left"
                  options={lineTypeOptions}
                  value={
                    lineTypeOptions.find((opt) => opt.id === lineType) || null
                  }
                  onChange={(option) => {
                    if (option) {
                      setLineType(option.id as ReportLineType);
                    }
                  }}
                  placeholder="Select line type..."
                  searchPlaceholder="Search line types..."
                />
                {lineType && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {LINE_TYPE_DESCRIPTIONS[lineType]}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Rebates by Supplier Tag</CardTitle>
            </CardHeader>
            <CardContent>
              <TableSkeleton />
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-destructive">
                Error loading report data. Please try again.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Report Table */}
        {report && !isLoading && (
          <ReportTableWrapper
            table={report.supplierTagTable}
            tableName={`${selectedSupplierTag?.label} Suppliers`}
            timeFilter={timeFilter}
            onTimeFilterChange={setTimeFilter}
            downloadFilename={`supplier-tag-report-${selectedYear}`}
            year={selectedYear}
          />
        )}
      </div>

      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
      />
      <SharedLinksManager
        open={linksManagerOpen}
        onClose={() => setLinksManagerOpen(false)}
        reportId={REPORT_ID}
        reportTitle={REPORT_TITLE}
        onCreateNew={() => {
          setLinksManagerOpen(false);
          setShareModalOpen(true);
        }}
      />
    </DashboardLayout>
  );
};

export default SupplierTagReportPage;
